import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { apiPost, setAccessToken, setUnauthorizedHandler } from '../api/http';
import { EP } from '../api/wire';
import { toHttpError } from '../api/errors';
import {
  clearStoredSession,
  decodeAccessExpiry,
  readAccessToken,
  readMe,
  readRefreshToken,
  refreshSession,
  storeSession,
  toMe,
  writeMe,
  type Me,
} from './authRefresh';
export type { Me };

interface AuthValue {
  me: Me | null;
  ready: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  patchMe: (next: Me) => void;
}
const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [ready, setReady] = useState(false);
  const clearSession = useCallback(() => {
    setAccessToken(null);
    clearStoredSession();
    setMe(null);
  }, []);
  /**
   * 应用一对新令牌 —— 落盘（含用户档案）+ 内存态。
   *
   * 档案要落盘，是因为 pipe 的 `/auth/me` 只回 `{id, role}`：完整用户行只有
   * token 响应才带。不存下来，启动时「access 未过期就不刷新」那条路就恢复
   * 不出用户，只能被踢回登录页。
   */
  const applyTokens = useCallback(
    (tokens: { access_token: string; refresh_token: string }, profile: Me | null) => {
      setAccessToken(tokens.access_token);
      // 档案先写、refresh token 后写 —— 别的标签以 refresh token 为「已被轮换」
      // 的提交点，它读到新 token 时一定也能读到配套档案（见 authRefresh）。
      storeSession(tokens.access_token, tokens.refresh_token, profile);
      if (profile !== null) setMe(profile);
    },
    [],
  );
  useEffect(() => {
    let cancelled = false;
    const accessToken = readAccessToken();
    const refreshToken = readRefreshToken();
    if (refreshToken === null) {
      // 没有 refresh token 就没有会话 —— 不发请求（省掉一次无谓的 401）。
      setReady(true);
      return;
    }
    /**
     * 启动**不再无条件刷新**。
     *
     * 旧实现每次挂载都打一次 `/auth/refresh`，多标签同时挂载就会并发用同一
     * 枚 token 刷新 → 后端按「重放」撤销整个 family → 全部标签被踢回登录页。
     *
     * 现在：access token 没过期就**直接恢复登录态**（档案从 `steam.me` 读），
     * 一个网络请求都不发；只有过期（或解不出来）才走加锁刷新。
     */
    const expiry = accessToken === null ? null : decodeAccessExpiry(accessToken);
    const accessAlive = accessToken !== null && expiry !== null && expiry > Date.now();
    if (accessAlive) {
      const profile = readMe();
      if (profile !== null) {
        setAccessToken(accessToken);
        setMe(profile);
        setReady(true);
        return;
      }
      // 令牌还活着，但没有用户档案（老版本留下的存量、或档案写坏了）。
      // 档案只能从 token 响应拿，所以借一次**加锁**刷新补齐 —— 不会跟别的
      // 标签打架（见 authRefresh 的单飞逻辑）。
    }
    void (async () => {
      try {
        const outcome = await refreshSession();
        switch (outcome.status) {
          case 'success': {
            setAccessToken(outcome.accessToken);
            if (outcome.me !== null) {
              if (!cancelled) setMe(outcome.me);
            } else {
              // 采纳了别的标签的令牌却拿不到档案 → 再兜底读一次盘；仍没有就
              // 当作「恢复不出登录态」，清场（否则会卡在一个半登录的怪状态）。
              const profile = readMe();
              if (profile !== null) {
                if (!cancelled) setMe(profile);
              } else if (!cancelled) {
                clearSession();
              }
            }
            return;
          }
          case 'rejected': {
            // 后端**明确拒绝**了 refresh token → 登录态确认失效 → 清场。
            if (!cancelled) clearSession();
            return;
          }
          case 'unavailable': {
            /**
             * 暂不可用（断网 / 后端重启 / 5xx / 限流）——**不清场**。
             *
             * 为什么这样是安全的：登录态此刻只是「**暂缓验证**」，不是
             * 「已确认失效」。后端没说过这枚 refresh token 废了，说的只是
             * 「这次没联系上」。把它当成失效来清场，等于让一次网络抖动把
             * 用户踢回登录页。
             *
             * 所以从盘上把登录态恢复出来：档案（`steam.me`）照旧，access
             * token 也照旧装上（**存量 token，可能已经过期**）。随后置
             * ready —— 首个 API 调用会 401，401 处理器再触发一次刷新；
             * 那时网络多半已经恢复，刷新成功，重放请求就通了。
             *
             * 就算网络还没恢复，最坏结果也只是「这次操作失败」，
             * 而不是「用户被登出」。
             */
            const storedAccess = readAccessToken();
            if (storedAccess !== null) setAccessToken(storedAccess);
            const profile = readMe();
            if (profile !== null && !cancelled) setMe(profile);
            return;
          }
        }
      } catch {
        // `refreshSession()` 内部已把失败全兜成三档，理论上不会抛到这里。
        // 真抛了也**不清场** —— 未知异常不等于「后端已明确拒绝」。
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clearSession]);
  /**
   * 标签内的并发去重 —— 同一个标签里多个请求同时 401，只刷一次。
   *
   * 跨标签的去重不在这里，而在 `authRefresh.refreshSession()`（Web Locks）。
   * 401 处理器与启动刷新**共用同一个加锁刷新函数**。
   */
  const refreshInFlight = useRef<Promise<boolean> | null>(null);
  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (refreshInFlight.current !== null) return refreshInFlight.current;
      const task = (async (): Promise<boolean> => {
        try {
          const outcome = await refreshSession();
          switch (outcome.status) {
            case 'success':
              setAccessToken(outcome.accessToken);
              if (outcome.me !== null) setMe(outcome.me);
              return true;
            case 'rejected':
              // 后端**明确拒绝**了 refresh token → 登录态确认失效 → 清场。
              clearSession();
              return false;
            case 'unavailable':
              /**
               * 暂不可用（断网 / 后端重启 / 5xx / 限流）→ **不清场**。
               *
               * 返回 `false` 让原始请求按现状抛它自己的错（就是那个 401），
               * 而登录态留在盘上：用户下一次操作会再撞一次 401、再触发一次
               * 刷新 —— 那时网络多半已经恢复，刷新成功，登录态自然续上。
               *
               * 关键在于「不清场」：把一次网络抖动翻译成「用户被登出」，
               * 代价（重新输密码）远大于「这次操作失败」。
               */
              return false;
          }
        } catch {
          // 未知异常同样**不清场** —— 不是「后端已明确拒绝」。
          return false;
        }
      })();
      refreshInFlight.current = task;
      void task.finally(() => {
        if (refreshInFlight.current === task) refreshInFlight.current = null;
      });
      return task;
    });
    return () => setUnauthorizedHandler(null);
  }, [clearSession]);
  const login = useCallback(
    async (username: string, password: string): Promise<void> => {
      try {
        const tokens = await apiPost(EP.authLogin, { body: { username, password } });
        applyTokens(tokens, toMe(tokens.user));
      } catch (cause) {
        clearSession();
        throw toHttpError(cause);
      }
    },
    [applyTokens, clearSession],
  );
  const logout = useCallback(async (): Promise<void> => {
    try {
      await apiPost(EP.authLogout, {
        body: { refresh_token: readRefreshToken() ?? '' },
      });
    } catch {
    } finally {
      clearSession();
    }
  }, [clearSession]);
  const patchMe = useCallback((next: Me) => {
    setMe(next);
    // 档案改了要同步落盘 —— 否则下次启动恢复出来的是旧档案。
    writeMe(next);
  }, []);
  const value = useMemo<AuthValue>(
    () => ({ me, ready, login, logout, patchMe }),
    [me, ready, login, logout, patchMe],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (value === null) throw new Error('useAuth 必须在 <AuthProvider> 内使用');
  return value;
}
